import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import AdminAutoPublishCard from "@/pages/admin/AdminAutoPublishCard";
import {
  Instagram, Facebook, CheckCircle2, AlertTriangle, XCircle, RefreshCw,
  Loader2, Zap, ExternalLink, Shield, Clock, Hash,
} from "lucide-react";

const STATE_META = {
  ok: { label: "Conectado", color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-300", Icon: CheckCircle2 },
  not_configured: { label: "No configurado", color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-300", Icon: AlertTriangle },
  token_expired: { label: "Token vencido", color: "text-red-700", bg: "bg-red-50", border: "border-red-300", Icon: XCircle },
  error: { label: "Error", color: "text-red-700", bg: "bg-red-50", border: "border-red-300", Icon: XCircle },
  untested: { label: "Sin probar", color: "text-slate-700", bg: "bg-slate-50", border: "border-slate-300", Icon: AlertTriangle },
};

function StateBadge({ state }) {
  const m = STATE_META[state] || STATE_META.untested;
  const Icon = m.Icon;
  return (
    <span
      data-testid={`social-state-badge-${state}`}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 border ${m.border} ${m.bg} ${m.color} text-[10px] uppercase tracking-widest font-bold`}
    >
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-AR", { timeZone: "America/Argentina/Ushuaia" });
  } catch { return iso; }
}

function fmtEpoch(secs) {
  if (!secs || typeof secs !== "number") return null;
  return new Date(secs * 1000).toLocaleString("es-AR", { timeZone: "America/Argentina/Ushuaia" });
}

function deriveState(platform) {
  if (!platform) return "untested";
  if (!platform.configured) return "not_configured";
  const r = platform.last_test;
  if (!r) return "untested";
  if (r.connected) return "ok";
  return r.status || "error";
}

function PlatformCard({
  title, icon: PlatIcon, brandColor,
  platform, envKeys, onTest, testing, testResult,
}) {
  const state = testResult ? (testResult.connected ? "ok" : (testResult.status || "error")) : deriveState(platform);
  const r = testResult || platform?.last_test || null;
  const isInstagram = title === "Instagram";
  return (
    <div
      data-testid={`social-card-${title.toLowerCase()}`}
      className="border border-softblue bg-white p-5 flex flex-col gap-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 flex items-center justify-center border border-softblue ${brandColor}`}>
            <PlatIcon className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="font-serif text-lg font-bold text-blueblack">{title}</div>
            <div className="text-xs text-textgray">
              {isInstagram
                ? (r?.username ? `@${r.username}` : "Cuenta @sur.primicia")
                : (r?.page_name || "Página Sur Primicia")}
            </div>
          </div>
        </div>
        <StateBadge state={state} />
      </div>

      {/* Details grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-xs">
        {isInstagram ? (
          <>
            <div className="flex items-center gap-2 text-textgray">
              <Hash className="w-3.5 h-3.5" /> Account ID:
              <span className="text-blueblack font-mono">
                {r?.account_id || platform?.account_id_hint || "—"}
              </span>
            </div>
            <div className="flex items-center gap-2 text-textgray">
              <Shield className="w-3.5 h-3.5" /> Publicación:
              <span className="text-blueblack font-medium">
                {r?.has_publish_permission === true && "OK"}
                {r?.has_publish_permission === false && "Falta permiso"}
                {r?.has_publish_permission == null && "—"}
              </span>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 text-textgray">
              <Hash className="w-3.5 h-3.5" /> Page ID:
              <span className="text-blueblack font-mono">
                {r?.page_id || platform?.page_id_hint || "—"}
              </span>
            </div>
            <div className="flex items-center gap-2 text-textgray">
              <Shield className="w-3.5 h-3.5" /> Publicación:
              <span className="text-blueblack font-medium">
                {r?.has_publish_permission === true && "OK"}
                {r?.has_publish_permission === false && "Falta permiso"}
                {r?.has_publish_permission == null && "—"}
              </span>
            </div>
          </>
        )}
        <div className="flex items-center gap-2 text-textgray">
          <Clock className="w-3.5 h-3.5" /> Última verificación:
          <span className="text-blueblack">{fmtDate(platform?.last_tested_at)}</span>
        </div>
        <div className="flex items-center gap-2 text-textgray">
          <ExternalLink className="w-3.5 h-3.5" /> Última publicación:
          <span className="text-blueblack">
            {platform?.last_publication?.published_at ? fmtDate(platform.last_publication.published_at) : "—"}
          </span>
        </div>
        {r?.token_expires_at != null && (
          <div className="flex items-center gap-2 text-textgray col-span-full">
            <Clock className="w-3.5 h-3.5" /> Vencimiento del token:
            <span className="text-blueblack">
              {r.token_expires_at === 0 ? "No expira" : (fmtEpoch(r.token_expires_at) || String(r.token_expires_at))}
            </span>
          </div>
        )}
        {Array.isArray(r?.permissions) && r.permissions.length > 0 && (
          <div className="col-span-full text-textgray">
            <div className="uppercase tracking-widest text-[10px] mb-1">Permisos detectados</div>
            <div className="flex flex-wrap gap-1">
              {r.permissions.map((p) => (
                <span key={p} className="px-1.5 py-0.5 border border-softblue text-[10px] text-blueblack font-mono">
                  {p}
                </span>
              ))}
            </div>
          </div>
        )}
        {!platform?.configured && Array.isArray(platform?.missing) && platform.missing.length > 0 && (
          <div className="col-span-full">
            <div className="uppercase tracking-widest text-[10px] text-amber-800 mb-1">Variables faltantes</div>
            <div className="flex flex-wrap gap-1">
              {platform.missing.map((k) => (
                <span key={k} className="px-1.5 py-0.5 border border-amber-400 bg-amber-50 text-[10px] text-amber-900 font-mono">
                  {k}
                </span>
              ))}
            </div>
          </div>
        )}
        {r?.detail && state !== "ok" && (
          <div className="col-span-full text-red-700 text-xs">
            {r.detail}
          </div>
        )}
      </div>

      <div className="pt-2 border-t border-softblue flex items-center gap-3">
        <Button
          data-testid={`social-test-${title.toLowerCase()}-btn`}
          onClick={onTest}
          disabled={testing}
          className="h-9 rounded-none bg-nightblue hover:bg-blueblack text-white text-[10px] uppercase tracking-widest font-bold px-4"
        >
          {testing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1.5" />}
          Probar conexión
        </Button>
        <div className="text-[10px] uppercase tracking-widest text-textgray">
          {envKeys.map((k) => (
            <span key={k} className="mr-2 font-mono">{k}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function AdminSocialSettingsPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [testingIG, setTestingIG] = useState(false);
  const [testingFB, setTestingFB] = useState(false);
  const [igResult, setIgResult] = useState(null);
  const [fbResult, setFbResult] = useState(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const data = await api.adminSocialStatus();
      setStatus(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo cargar el estado de Redes Sociales");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(false); }, [load]);

  const testIG = async () => {
    setTestingIG(true);
    try {
      const r = await api.adminSocialInstagramTest();
      setIgResult(r);
      if (r.connected) toast.success(`Instagram conectado · @${r.username || "cuenta"}`);
      else if (r.status === "not_configured") toast.warning("Instagram pendiente de configuración");
      else toast.error(r.detail || "No se pudo conectar con Instagram");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Fallo el test de Instagram");
    } finally {
      setTestingIG(false);
      load(true);
    }
  };

  const testFB = async () => {
    setTestingFB(true);
    try {
      const r = await api.adminSocialFacebookTest();
      setFbResult(r);
      if (r.connected) toast.success(`Facebook conectado · ${r.page_name || "Página"}`);
      else if (r.status === "not_configured") toast.warning("Facebook pendiente de configuración");
      else toast.error(r.detail || "No se pudo conectar con Facebook");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Fallo el test de Facebook");
    } finally {
      setTestingFB(false);
      load(true);
    }
  };

  return (
    <div data-testid="admin-social-settings-page" className="max-w-6xl">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <Instagram className="w-5 h-5 text-nightblue" />
            <h1 className="font-serif text-2xl font-bold text-blueblack">Redes Sociales</h1>
          </div>
          <p className="text-sm text-textgray mt-1">
            Publicación directa en Instagram y Facebook a través de la API oficial de Meta.
          </p>
        </div>
        <Button
          data-testid="social-refresh-btn"
          onClick={() => load(true)}
          disabled={loading || refreshing}
          variant="outline"
          className="h-9 rounded-none border-softblue text-nightblue text-[11px] uppercase tracking-widest font-semibold"
        >
          {refreshing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
          Refrescar
        </Button>
      </div>

      {loading && !status && (
        <div className="border border-softblue bg-white p-8 text-center text-textgray text-sm">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Cargando estado…
        </div>
      )}

      {status && (
        <>
          {/* Expired-token alert */}
          {(() => {
            const igExp = status.instagram?.last_test?.error_code === "token_expired";
            const fbExp = status.facebook?.last_test?.error_code === "token_expired";
            if (!igExp && !fbExp) return null;
            return (
              <div
                data-testid="social-token-expired-banner"
                className="border-2 border-red-500 bg-red-50 p-3 mb-4 flex items-start gap-3"
              >
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 text-xs">
                  <div className="font-bold text-red-800 uppercase tracking-widest text-[11px] mb-1">
                    Token vencido — la publicación en {igExp && fbExp ? "Instagram y Facebook" : (igExp ? "Instagram" : "Facebook")} está bloqueada
                  </div>
                  <div className="text-red-900 leading-relaxed">
                    {fbExp && (
                      <p className="mb-1">
                        <b>Facebook</b>: el Page Access Token expiró. Los tokens generados desde Graph API Explorer son <b>de corta duración</b> (~2 horas). Necesitás un <b>token de larga duración</b>:
                      </p>
                    )}
                    <ol className="list-decimal pl-5 space-y-1 text-red-900/90">
                      <li>Andá a <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noopener noreferrer" className="underline font-semibold">Graph API Explorer</a>, seleccioná la app &quot;Sur Primicia Social Publisher&quot; y generá un User Token con permisos <span className="font-mono">pages_show_list, pages_manage_posts, pages_read_engagement, business_management</span>.</li>
                      <li>Convertilo a long-lived: <span className="font-mono text-[11px] break-all">GET /oauth/access_token?grant_type=fb_exchange_token&client_id=APP_ID&client_secret=APP_SECRET&fb_exchange_token=USER_TOKEN</span></li>
                      <li>Con ese user token de 60 días, pedí los Page Tokens: <span className="font-mono text-[11px]">GET /me/accounts?access_token=LONG_LIVED_USER_TOKEN</span> — el <b>page access_token</b> del array NO EXPIRA (es &quot;never&quot; para páginas que administrás con tu app instalada).</li>
                      <li>Pegámelo por chat o cargalo como Secret del pod: <span className="font-mono">META_FACEBOOK_PAGE_ACCESS_TOKEN</span>. Después presioná <b>Probar conexión</b> en la tarjeta.</li>
                    </ol>
                  </div>
                </div>
              </div>
            );
          })()}

          <div className="border border-softblue bg-lightgray/50 p-3 mb-5 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-textgray">
            <div>
              <span className="uppercase tracking-widest text-[10px] mr-1">Graph API</span>
              <span className="font-mono text-blueblack">{status.graph_api_version}</span>
            </div>
            <div>
              <span className="uppercase tracking-widest text-[10px] mr-1">Modo prueba</span>
              <span
                data-testid="social-test-mode"
                className={`font-mono ${status.test_mode ? "text-amber-700" : "text-emerald-700"}`}
              >
                {status.test_mode ? "ACTIVO (no publica real)" : "APAGADO (publica real)"}
              </span>
            </div>
            <div>
              <span className="uppercase tracking-widest text-[10px] mr-1">Verificado</span>
              <span className="text-blueblack">{fmtDate(status.generated_at)}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <PlatformCard
              title="Instagram"
              icon={Instagram}
              brandColor="bg-gradient-to-br from-pink-500 via-red-500 to-yellow-500"
              platform={status.instagram}
              envKeys={["META_INSTAGRAM_ACCESS_TOKEN", "META_INSTAGRAM_ACCOUNT_ID"]}
              onTest={testIG}
              testing={testingIG}
              testResult={igResult}
            />
            <PlatformCard
              title="Facebook"
              icon={Facebook}
              brandColor="bg-[#1877F2]"
              platform={status.facebook}
              envKeys={["META_FACEBOOK_PAGE_ACCESS_TOKEN", "META_FACEBOOK_PAGE_ID"]}
              onTest={testFB}
              testing={testingFB}
              testResult={fbResult}
            />
          </div>

          <div className="mt-6">
            <AdminAutoPublishCard />
          </div>

          <div className="mt-6 border border-softblue bg-white p-4 text-xs text-textgray">
            <div className="font-semibold text-blueblack mb-2">Cómo cargar los tokens</div>
            <ol className="list-decimal pl-5 space-y-1">
              <li>Solicitá al administrador de la app Meta los valores de:
                <span className="ml-1 font-mono text-blueblack">META_APP_ID</span>,
                <span className="ml-1 font-mono text-blueblack">META_APP_SECRET</span>,
                <span className="ml-1 font-mono text-blueblack">META_INSTAGRAM_ACCESS_TOKEN</span>,
                <span className="ml-1 font-mono text-blueblack">META_FACEBOOK_PAGE_ID</span>,
                <span className="ml-1 font-mono text-blueblack">META_FACEBOOK_PAGE_ACCESS_TOKEN</span>.
              </li>
              <li>Cargalos como <b>Secrets</b> en el pod del backend (nunca en el frontend).</li>
              <li>Volvé a esta pantalla y presioná <b>Probar conexión</b> en cada tarjeta.</li>
              <li>Cuando ambas queden en <b>Conectado</b>, activá el interruptor <span className="font-mono">SOCIAL_PUBLISHING_TEST_MODE=false</span> para permitir publicaciones reales.</li>
            </ol>
          </div>
        </>
      )}
    </div>
  );
}
